/**
 * Generic utilities for handling batch API operations with partial failures.
 * These utilities are domain-agnostic and work with any batch response format.
 */

/**
 * Status enum for batch operation results.
 * Provides type-safe discrimination of operation outcomes.
 */
export enum BatchOperationStatus {
  /** All items were processed successfully */
  Success = 'SUCCESS',
  /** Some items succeeded, some failed */
  Partial = 'PARTIAL',
  /** All items failed */
  Failure = 'FAILURE',
}

/**
 * Generic batch response type that matches our API convention.
 * TFailure must have an `error` property containing the error details.
 */
export type BatchResponse<TError, TFailure extends {
  error: TError;
} = {
  error: TError;
}> = {
  success: string[];
  failures: TFailure[];
};

/**
 * Result type for batch operations that can have partial failures.
 * Discriminated union using BatchOperationStatus makes it easy to handle each case.
 */
export type BatchOperationResult<TError> = {
  status: BatchOperationStatus.Success;
  successCount: number;
} | {
  status: BatchOperationStatus.Partial;
  successCount: number;
  failureCount: number;
  errors: TError[];
} | {
  status: BatchOperationStatus.Failure;
  failureCount: number;
  errors: TError[];
};

/**
 * Analyzes a batch operation response and returns a typed result.
 * This helps consumers handle all three cases (success, partial, failure) explicitly.
 *
 * @param response - The batch operation response from the API
 * @returns A discriminated union result indicating the operation outcome
 *
 * @example
 * const result = analyzeBatchResult(response)
 * if (result.status === BatchOperationStatus.Success) {
 *   notification.success({ content: 'All items processed' })
 * } else if (result.status === BatchOperationStatus.Partial) {
 *   notification.warning({ description: `${result.successCount} succeeded, ${result.failureCount} failed` })
 * } else {
 *   notification.error({ description: 'All operations failed' })
 * }
 */
export function analyzeBatchResult<TError, TFailure extends {
  error: TError;
}>(response: BatchResponse<TError, TFailure>): BatchOperationResult<TError> {
  const successCount = response.success.length;
  const failureCount = response.failures.length;
  const errors = response.failures.map(f => f.error);
  if (failureCount === 0) {
    return {
      status: BatchOperationStatus.Success,
      successCount
    };
  }
  if (successCount === 0) {
    return {
      status: BatchOperationStatus.Failure,
      failureCount,
      errors
    };
  }
  return {
    status: BatchOperationStatus.Partial,
    successCount,
    failureCount,
    errors
  };
}

/**
 * Individual item status in a batch operation result.
 * Used for displaying per-item results in the modal table.
 */
export type BatchResultItem<TError = unknown> = {
  id: string;
  /** Display label for the item (e.g., building name, address, employee name) */
  label: string;
  success: boolean;
  error?: TError;
};

/**
 * Data structure for batch result display components (modal/banner).
 * This is a normalized format suitable for UI presentation.
 */
export type BatchResultDisplayData<TError = unknown> = {
  status: BatchOperationStatus;
  successCount: number;
  failureCount: number;
  errors: TError[];
  /** Per-item results for detailed display in modal */
  items?: BatchResultItem<TError>[];
};

/**
 * Converts a batch API response to a display-ready data structure.
 * Use this to transform API responses for BatchResultModal or BatchResultBanner.
 *
 * @param response - The batch operation response from the API
 * @returns Normalized data structure for display components
 *
 * @example
 * const response = await batchDeleteBuildings(body)
 * const displayData = toBatchResultDisplayData(response)
 * setBatchResult(displayData)
 */
export function toBatchResultDisplayData<TError, TFailure extends {
  error: TError;
}>(response: BatchResponse<TError, TFailure>): BatchResultDisplayData<TError> {
  const result = analyzeBatchResult(response);
  const errors = response.failures.map(f => f.error);
  if (result.status === BatchOperationStatus.Success) {
    return {
      status: result.status,
      successCount: result.successCount,
      failureCount: 0,
      errors
    };
  }
  if (result.status === BatchOperationStatus.Failure) {
    return {
      status: result.status,
      successCount: 0,
      failureCount: result.failureCount,
      errors
    };
  }
  return {
    status: result.status,
    successCount: result.successCount,
    failureCount: result.failureCount,
    errors
  };
}

/**
 * Item with id and label for correlating batch results with original selection.
 */
export type BatchItemInfo = {
  id: string;
  /** Display label for the item (e.g., building name, address, employee name) */
  label: string;
};

/**
 * Converts a batch API response to a display-ready data structure with per-item details.
 * Use this when you have access to the original selected items and want to show
 * individual item status in the result modal.
 *
 * @param response - The batch operation response from the API
 * @param selectedItems - The original selected items with id and name
 * @param getFailureId - Function to extract item ID from a failure object (default: looks for common patterns)
 * @returns Normalized data structure with per-item results for detailed display
 *
 * @example
 * const response = await batchDeleteBuildings({ buildingIds })
 * const displayData = toBatchResultDisplayDataWithItems(
 *   response,
 *   selectedRows.map(r => ({ id: r.id, name: r.name })),
 *   (failure) => failure.buildingId
 * )
 * setBatchResult(displayData)
 */
export function toBatchResultDisplayDataWithItems<TError, TFailure extends {
  error: TError;
} & Record<string, unknown>>(response: BatchResponse<TError, TFailure>, selectedItems: BatchItemInfo[], getFailureId: (failure: TFailure) => string): BatchResultDisplayData<TError> {
  const baseResult = toBatchResultDisplayData(response);

  // Build a map of failure id -> error for quick lookup
  const failureMap = new Map(response.failures.map(f => [getFailureId(f), f.error]));

  // Create items array with all selected items and their status
  // Note: No sorting needed - the modal groups items by success/failure separately
  const items: BatchResultItem<TError>[] = selectedItems.map(item => {
    const error = failureMap.get(item.id);
    return {
      id: item.id,
      label: item.label || item.id,
      success: !error,
      error
    };
  });
  return {
    ...baseResult,
    items
  };
}